import React, { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { formatApiError } from '@/lib/validators';
import { Lock, User, Eye, EyeOff } from 'lucide-react';

// Logo URL from environment variable, with fallback to default
const LOGO_URL = process.env.REACT_APP_LOGO_URL || "/logo.png";
const TAGLINE = 'Label Manufacturer & Printing Company';

export default function LoginPage() {
  const { login } = useAuth();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!username || !password) {
      toast.error('Please enter username and password');
      return;
    }
    setLoading(true);
    try {
      await login(username, password);
      toast.success('Login successful');
    } catch (err) {
      toast.error(formatApiError(err, err?.message || 'Login failed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen" data-testid="login-page">
      {/* Left: Branding */}
      <div className="hidden lg:flex lg:w-1/2 relative flex-col items-center justify-center p-12 bg-gradient-to-br from-slate-50 via-white to-blue-50/50 overflow-hidden">
        {/* Atmospheric blur orbs */}
        <div className="absolute top-20 -left-20 w-72 h-72 rounded-full bg-brand-blue/5 blur-3xl" />
        <div className="absolute bottom-20 right-10 w-80 h-80 rounded-full bg-brand-gold/10 blur-3xl" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 rounded-full bg-brand-blue/[0.03] blur-3xl" />
        <div className="relative z-10 flex flex-col items-center text-center max-w-md">
          <img src={LOGO_URL} alt="Labelify Private Limited" className="h-24 w-auto object-contain" />
          <p className="mt-8 text-lg leading-relaxed text-slate-600 font-body">
            {TAGLINE}
          </p>
          <div className="mt-8 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-brand-blue/5 border border-brand-blue/10">
            <span className="text-xs font-medium tracking-wide text-brand-blue">Operations Management System</span>
          </div>
        </div>
      </div>

      {/* Right: Login Form */}
      <div className="flex-1 flex items-center justify-center p-6 bg-slate-50/50">
        <div className="w-full max-w-md">
          {/* Mobile logo */}
          <div className="lg:hidden text-center mb-8">
            <img src={LOGO_URL} alt="Labelify Private Limited" className="h-16 w-auto mx-auto object-contain" />
            <p className="mt-4 text-sm text-slate-500 font-body">{TAGLINE}</p>
          </div>

          <div className="bg-white rounded-2xl shadow-card border border-slate-100 p-8">
            <div className="mb-6">
              <h2 className="font-heading text-2xl font-extrabold text-slate-900" data-testid="login-title">
                Welcome back
              </h2>
              <p className="text-sm text-slate-500 mt-1 font-body">
                Sign in to your account to continue
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="space-y-2">
                <Label htmlFor="username" className="font-body font-medium text-slate-700">Username</Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    id="username"
                    data-testid="login-username-input"
                    className="pl-10"
                    placeholder="Enter username"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    autoComplete="username"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="font-body font-medium text-slate-700">Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    id="password"
                    data-testid="login-password-input"
                    type={showPassword ? 'text' : 'password'}
                    className="pl-10 pr-10"
                    placeholder="Enter password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    autoComplete="current-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 focus:outline-none"
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              <Button
                type="submit"
                data-testid="login-submit-btn"
                className="w-full h-11 bg-gradient-to-r from-brand-blue to-brand-blue-dark hover:-translate-y-0.5 shadow-button-brand hover:shadow-lg text-white font-heading font-semibold rounded-lg transition-all duration-200"
                disabled={loading}
              >
                {loading ? 'Signing in...' : 'Sign In'}
              </Button>
            </form>
          </div>

          <p className="text-center text-xs text-slate-400 mt-6 font-body">
            Labelify Private Limited
          </p>
        </div>
      </div>
    </div>
  );
}
